import React, { useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { CrewPilotNavList, CrewPilotNavRow } from "../CrewPilotNavRow";
import { CPDropdown } from "../CPDropdown";
import { usePermissions } from "../../hooks/usePermissions";
import {
  addCustomEstimateWorkType,
  addEstimateWorkType,
  CATALOG_OTHER_WORK_TYPE,
} from "../../helpers/estimates";
import { addEstimateWorkTypeFromTemplate } from "../../helpers/workTypeTemplates";
import { WorkTypeNameDialog } from "./WorkTypeNameDialog";
import "./estimateWorkspace.css";

const MODE = {
  CATALOG: "catalog",
  TEMPLATE: "template",
};

/**
 * Add Feature is a workflow/navigation choice (catalog vs. saved template
 * vs. custom name), not small data entry, so it is a dedicated screen
 * rather than a bottom sheet. Selecting Custom Feature still opens a small
 * focused name-entry sheet, because THAT is small data entry. Preserves the
 * exact three existing creation paths and helpers — no new persistence, no
 * Template schema/behavior changes.
 */
export function AddFeatureScreen({
  locationName,
  roomId,
  featureTypes,
  workTypeTemplates = [],
  organizationId = null,
  onBack,
  onCreated,
}) {
  const { actor } = usePermissions();
  const [mode, setMode] = useState(null);
  const [featureTypeId, setFeatureTypeId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleAddFromCatalog() {
    if (!featureTypeId) return;
    setSaving(true);
    setError("");
    try {
      const created = await addEstimateWorkType(actor, roomId, featureTypeId);
      await onCreated?.(created);
      onBack?.();
    } catch (err) {
      setError(err.message || "Unable to add feature.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddFromTemplate() {
    if (!templateId) return;
    setSaving(true);
    setError("");
    try {
      const created = await addEstimateWorkTypeFromTemplate(
        roomId,
        templateId,
        organizationId,
      );
      await onCreated?.(created);
      onBack?.();
    } catch (err) {
      setError(err.message || "Unable to add feature from template.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveCustom(name) {
    const created = await addCustomEstimateWorkType(actor, roomId, name);
    await onCreated?.(created);
    onBack?.();
  }

  const featureTypeOptions = featureTypes.map((ft) => ({
    value: ft.feature_type_id,
    label: ft.name,
  }));

  return (
    <CrewPilotModuleShell title="Add Feature" subtitle={locationName} onBack={onBack} desktopBackLabel={locationName}>
      {error && <div className="est-inline-error">{error}</div>}

      <CrewPilotNavList>
        <CrewPilotNavRow
          title="From Catalog"
          subtitle="Choose an existing feature type"
          onClick={() => setMode(mode === MODE.CATALOG ? null : MODE.CATALOG)}
        />
        {workTypeTemplates.length > 0 && (
          <CrewPilotNavRow
            title="From Template"
            subtitle="Reuse a saved Feature configuration"
            onClick={() => setMode(mode === MODE.TEMPLATE ? null : MODE.TEMPLATE)}
          />
        )}
        <CrewPilotNavRow
          title="Custom Feature"
          subtitle="Create your own Feature"
          onClick={() => setShowCustomDialog(true)}
        />
      </CrewPilotNavList>

      {mode === MODE.CATALOG && (
        <div className="est-home-edit-panel">
          <CPDropdown
            label="Catalog Feature"
            value={featureTypeId}
            onChange={(value) => {
              if (value === CATALOG_OTHER_WORK_TYPE) {
                setFeatureTypeId("");
                setMode(null);
                setShowCustomDialog(true);
                return;
              }
              setFeatureTypeId(value);
            }}
            options={[
              ...featureTypeOptions,
              { value: CATALOG_OTHER_WORK_TYPE, label: "Custom Feature…" },
            ]}
            placeholder="Select a feature type"
          />
          {featureTypeId && (
            <button
              type="button"
              className="est-sheet-btn est-sheet-btn--primary"
              onClick={handleAddFromCatalog}
              disabled={saving}
            >
              {saving ? "Adding…" : "Add Feature"}
            </button>
          )}
        </div>
      )}

      {mode === MODE.TEMPLATE && (
        <div className="est-home-edit-panel">
          <CPDropdown
            label="Saved Template"
            value={templateId}
            onChange={setTemplateId}
            options={workTypeTemplates.map((template) => ({
              value: template.work_type_template_id,
              label: template.template_name,
            }))}
            placeholder="Select a saved template"
          />
          {templateId && (
            <button
              type="button"
              className="est-sheet-btn est-sheet-btn--primary"
              onClick={handleAddFromTemplate}
              disabled={saving}
            >
              {saving ? "Adding…" : "Add from Template"}
            </button>
          )}
        </div>
      )}

      <div className="est-bottom-spacer" />

      {showCustomDialog && (
        <WorkTypeNameDialog
          title="Custom Feature"
          fieldLabel="Feature Name"
          initialName=""
          onSave={handleSaveCustom}
          onClose={() => setShowCustomDialog(false)}
        />
      )}
    </CrewPilotModuleShell>
  );
}
